import React, { createContext, useContext, useMemo, useState } from "react";

type Locale = "da" | "en";

type Dictionary = { [key: string]: string | Dictionary };

const dictionaries: Record<Locale, Dictionary> = {
  da: {
    common: {
      brand: "Tryg Hånd",
      skipToContent: "Spring til indhold",
  languageToggle: "Skift sprog",
  mobileNavLabel: "Mobil navigation",
  navVikarDiary: "Dagbog",
  navPortalChooser: "Portaler",
  navKommuneNewRequest: "Ny anmodning",
  navKommuneRequests: "Anmodninger",
  navKommuneDocuments: "Dokumenter",
  navKommuneReports: "Rapporter",
  navDashboard: "Overblik",
      navNewMedarbejder: "Ny medarbejder",
      navNewVirksomhed: "Ny virksomhed",
      navAdmin: "Admin",
      navLogin: "Log ind",
    },
    entry: {
      badge: "Velkommen",
      title: "Vikarbureau specialiseret i børn og unge",
      subtitle:
        "Vælg den portal, der matcher din rolle, for at logge ind eller oprette dig.",
      kommuneTitle: "Kommunelogin",
      kommuneDescription:
        "Adgang for kommuner og samarbejdspartnere.",
      dagbogTitle: "Dagbog / Vikar",
      dagbogDescription:
        "Log ind for pædagoger, socialpædagoger og vikarer specialiseret i børn og unge.",
      heroCta: "Log ind",
      heroStatTeams: "Kommuner & partnere",
      heroStatCases: "Forløb håndteret",
      heroStatSatisfaction: "Oppetid & SLA",
      choosePortal: "Vælg portal",
      portalSubtitle: "Portalerne kræver godkendte loginoplysninger.",
      adminLogin: "Administrator login",
  footer: "Kontakt kontakt@tryghaand.dk ved spørgsmål.",
      backToChooser: "Tilbage til portalvælger",
      featureSecurityTitle: "Sikkerhed først",
      featureSecurityDescription:
        "Adgangsstyring, to-faktor og datakryptering beskytter alle sager.",
      featureDataTitle: "Datagrundlag i realtid",
      featureDataDescription:
        "Rapportering og journaler opdateres automatisk for teamet.",
      featureSupportTitle: "Support der svarer",
      featureSupportDescription:
        "Vores team hjælper kommuner og partnere alle hverdage 08-18.",
      complianceHeading: "Bygget til sikker drift",
      complianceDescription:
        "Min Tryg Hånd kører i EU-datacentre og følger ISO27001-aligned processer samt GDPR-principper.",
      supportHeading: "Har du brug for hjælp?",
      supportDescription:
        "Kontakt os for opsætning, onboarding eller tekniske spørgsmål.",
      supportEmailLabel: "Support",
    },
    auth: {
      loginTitle: "Log ind",
      loginDescription: "Angiv din email og adgangskode for at fortsætte.",
      noAccount: "Ingen konto?",
      signupLink: "Opret bruger",
      twoFactorTitle: "Tovejskode",
      emailLabel: "Email",
      passwordLabel: "Adgangskode",
      confirmPasswordLabel: "Bekræft adgangskode",
      loginButton: "Log ind",
      passwordMismatch: "Adgangskoderne matcher ikke.",
      verifying: "Bekræfter…",
      loggingIn: "Logger ind…",
      creatingAccount: "Opretter…",
      resendCode: "Send ny kode",
      back: "Tilbage",
      confirmCode: "Bekræft kode",
      twoFactorInstructions:
        "Indtast den 6-cifrede kode, vi har sendt til {email}.",
      createAccountTitle: "Opret bruger",
      createAccountDescription:
        "Udfyld oplysningerne for at oprette en konto på Min Tryg Hånd.",
      firstNameLabel: "Fornavn",
      lastNameLabel: "Efternavn",
      signupButton: "Opret konto",
      accountExists: "Har du allerede en konto?",
      goToLogin: "Gå til login",
    },
    dashboard: {
      title: "Velkommen {name}",
      intro:
        "Vælg en handling herunder for at fortsætte dit arbejde i Min Tryg Hånd.",
      roleLabel: "Rolle",
      logout: "Log ud",
      actions: {
        adminManageUsersLabel: "Administrer brugere",
        adminManageUsersDescription:
          "Se brugere, opdater roller og overvåg audit log.",
        adminNewMedarbejderLabel: "Ny medarbejder",
        adminNewMedarbejderDescription:
          "Opret medarbejdere og vedhæft nødvendig dokumentation.",
        adminNewVirksomhedLabel: "Ny virksomhed",
        adminNewVirksomhedDescription:
          "Registrer samarbejdspartnere og gem aftaler.",
        kommuneNewVirksomhedLabel: "Ny virksomhed",
        kommuneNewVirksomhedDescription:
          "Opret en ny samarbejdspartner eller kommune.",
        kommuneNewMedarbejderLabel: "Ny medarbejder",
        kommuneNewMedarbejderDescription:
          "Registrer medarbejdere til igangværende sager.",
        vikarUploadDocsLabel: "Upload dokumenter",
        vikarUploadDocsDescription:
          "Opdater dine oplysninger og nødvendige attester.",
      },
    },
    admin: {
      title: "Administrator",
      description: "Administrer brugere, roller og audit-log.",
      usersHeading: "Brugere",
      auditHeading: "Audit log",
      nameHeader: "Navn",
      emailHeader: "Email",
      roleHeader: "Rolle",
      loading: "Indlæser…",
      createdHeader: "Oprettet",
      actionsHeader: "Handlinger",
      deleteAction: "Deaktiver",
      emptyState: "Ingen brugere fundet.",
      auditEmpty: "Ingen auditlogposter.",
      roleOptionAdmin: "Admin",
      roleOptionKommune: "Kommune",
      roleOptionVikar: "Vikar",
      roleOptionCoordinator: "Koordinator",
      roleChangeConfirm: "Er du sikker på, at du vil ændre {email}s rolle til {role}?",
      roleChangeSuccess: "Rollen blev opdateret.",
      roleChangeError: "Kunne ikke opdatere rollen.",
      deleteConfirm: "Er du sikker på, at du vil deaktivere {email}? Brugeren kan genskabes senere.",
      deleteSuccess: "Brugeren er deaktiveret.",
      deleteError: "Kunne ikke deaktivere brugeren.",
      errorToastTitle: "Noget gik galt",
    },
    forms: {
      medarbejderTitle: "Ny medarbejder",
      medarbejderSubtitle:
        "Registrér en ny medarbejder og vedhæft den nødvendige dokumentation.",
      virksomhedTitle: "Ny virksomhed",
      virksomhedSubtitle:
        "Opret en ny samarbejdspartner og gem aftaledokumenter.",
      submit: "Indsend",
      submitting: "Indsender…",
      success: "Oprettelsen lykkedes.",
    },
  },
  en: {
    common: {
      brand: "Tryg Hånd",
      skipToContent: "Skip to content",
  languageToggle: "Toggle language",
  mobileNavLabel: "Mobile navigation",
  navVikarDiary: "Diary",
  navPortalChooser: "Portals",
  navKommuneNewRequest: "New request",
  navKommuneRequests: "Requests",
  navKommuneDocuments: "Documents",
  navKommuneReports: "Reports",
      navDashboard: "Dashboard",
      navNewMedarbejder: "New employee",
      navNewVirksomhed: "New organisation",
      navAdmin: "Admin",
      navLogin: "Log in",
    },
    entry: {
      badge: "Welcome",
      title: "Temp agency specialized in children and youth",
      subtitle:
        "Choose the portal that matches your role to log in or sign up.",
      kommuneTitle: "Municipality login",
      kommuneDescription:
        "Access for municipalities and partner organisations.",
      dagbogTitle: "Diary / Temp staff",
      dagbogDescription:
        "Sign in for educators, social pedagogues and temporary staff specialized in children and youth.",
      heroCta: "Sign in",
      heroStatTeams: "Municipal teams",
      heroStatCases: "Cases processed",
      heroStatSatisfaction: "Uptime & SLA",
      choosePortal: "Choose portal",
      portalSubtitle: "Secure access for authorised users only.",
      adminLogin: "Administrator login",
  footer: "Contact kontakt@tryghaand.dk for assistance.",
      backToChooser: "Back to portal chooser",
      featureSecurityTitle: "Security by design",
      featureSecurityDescription:
        "Role-based access, MFA, and encryption protect every record.",
      featureDataTitle: "Live operational data",
      featureDataDescription:
        "Dashboards and journals stay in sync for the entire team.",
      featureSupportTitle: "Responsive support",
      featureSupportDescription:
        "Our specialists assist municipalities and partners Monday–Friday, 08–18 CET.",
      complianceHeading: "Enterprise-grade compliance",
      complianceDescription:
        "Hosted in EU data centres with ISO27001-aligned controls and GDPR best practices.",
      supportHeading: "Need assistance?",
      supportDescription:
        "We’re here to help with rollout, onboarding, or technical questions.",
      supportEmailLabel: "Support",
    },
    auth: {
      loginTitle: "Sign in",
      loginDescription: "Enter your email and password to continue.",
      noAccount: "Need an account?",
      signupLink: "Create account",
      twoFactorTitle: "Two-factor code",
      emailLabel: "Email",
      passwordLabel: "Password",
      confirmPasswordLabel: "Confirm password",
      loginButton: "Sign in",
      passwordMismatch: "Passwords do not match.",
      verifying: "Verifying…",
      loggingIn: "Signing in…",
      creatingAccount: "Creating…",
      resendCode: "Send new code",
      back: "Back",
      confirmCode: "Confirm code",
      twoFactorInstructions:
        "Enter the 6-digit code we sent to {email}.",
      createAccountTitle: "Create account",
      createAccountDescription:
        "Fill in your details to create a Min Tryg Hånd account.",
      firstNameLabel: "First name",
      lastNameLabel: "Last name",
      signupButton: "Create account",
      accountExists: "Already have an account?",
      goToLogin: "Go to sign in",
    },
    dashboard: {
      title: "Welcome {name}",
      intro:
        "Pick an action below to continue your work in Min Tryg Hånd.",
      roleLabel: "Role",
      logout: "Sign out",
      actions: {
        adminManageUsersLabel: "Manage users",
        adminManageUsersDescription:
          "Review users, adjust roles, and monitor the audit trail.",
        adminNewMedarbejderLabel: "New employee",
        adminNewMedarbejderDescription:
          "Create employee records and attach required documents.",
        adminNewVirksomhedLabel: "New organisation",
        adminNewVirksomhedDescription:
          "Register partner organisations and agreements.",
        kommuneNewVirksomhedLabel: "New organisation",
        kommuneNewVirksomhedDescription:
          "Create a new partner or municipality record.",
        kommuneNewMedarbejderLabel: "New employee",
        kommuneNewMedarbejderDescription:
          "Register employees for active cases.",
        vikarUploadDocsLabel: "Upload documents",
        vikarUploadDocsDescription:
          "Update your profile and required certificates.",
      },
    },
    admin: {
      title: "Administrator",
      description: "Manage users, roles, and the audit trail.",
      usersHeading: "Users",
      auditHeading: "Audit log",
      nameHeader: "Name",
      emailHeader: "Email",
      roleHeader: "Role",
      loading: "Loading…",
      createdHeader: "Created",
      actionsHeader: "Actions",
      deleteAction: "Deactivate",
      emptyState: "No users found.",
      auditEmpty: "No audit entries yet.",
      roleOptionAdmin: "Admin",
      roleOptionKommune: "Municipality",
      roleOptionVikar: "Temp",
      roleOptionCoordinator: "Coordinator",
      roleChangeConfirm: "Change {email}'s role to {role}?",
      roleChangeSuccess: "Role updated.",
      roleChangeError: "Could not update the role.",
      deleteConfirm: "Deactivate {email}? You can restore the user later.",
      deleteSuccess: "User deactivated.",
      deleteError: "Could not deactivate the user.",
      errorToastTitle: "Something went wrong",
    },
    forms: {
      medarbejderTitle: "New employee",
      medarbejderSubtitle:
        "Register a new employee and attach the required documentation.",
      virksomhedTitle: "New organisation",
      virksomhedSubtitle:
        "Create a new partner organisation and store agreements.",
      submit: "Submit",
      submitting: "Submitting…",
      success: "Created successfully.",
    },
  },
};

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: Record<string, string>) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const resolveKey = (dictionary: Dictionary, key: string): string | undefined => {
  return key.split(".").reduce<string | Dictionary | undefined>((acc, part) => {
    if (acc && typeof acc === "object" && part in acc) {
      return (acc as Dictionary)[part];
    }
    return undefined;
  }, dictionary) as string | undefined;
};

const replaceVars = (value: string, vars?: Record<string, string>) => {
  if (!vars) return value;
  return Object.entries(vars).reduce(
    (acc, [token, replacement]) =>
      acc.replaceAll(`{${token}}`, replacement ?? ""),
    value,
  );
};

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  const defaultLocale =
    (import.meta.env.VITE_I18N_DEFAULT as Locale | undefined) ?? "da";
  const [locale, setLocale] = useState<Locale>(
    defaultLocale === "en" ? "en" : "da",
  );

  const value = useMemo<I18nContextValue>(() => {
    const translate = (key: string, vars?: Record<string, string>) => {
      const message =
        resolveKey(dictionaries[locale], key) ??
        resolveKey(dictionaries.da, key) ??
        key;
      return typeof message === "string" ? replaceVars(message, vars) : key;
    };
    return {
      locale,
      setLocale,
      t: translate,
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
};
